import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES } from '@/i18n'
import { LoginDialog } from '@/components/LoginDialog'
import { api } from '@/lib/api'
import { AboutSections } from './AboutPage'

/* ─── Shared primitives（设计稿配色） ─── */
const CEMENT = '#EBEBEB'
const CARBON = '#111111'
const MID = '#555555'
const PALE = '#D4D4D4'

function PillBadge({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase"
      style={{
        background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(17,17,17,0.08)',
        color: dark ? '#ffffff' : CARBON,
        border: dark ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(17,17,17,0.14)',
      }}
    >
      {children}
    </span>
  )
}

function SectionBar({ badge, index, dark = false }: { badge: string; index: string; dark?: boolean }) {
  return (
    <div className="flex items-center justify-between mb-8">
      <PillBadge dark={dark}>{badge}</PillBadge>
      <span
        className="text-5xl font-black leading-none select-none"
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          color: dark ? 'rgba(255,255,255,0.15)' : 'rgba(17,17,17,0.12)',
          letterSpacing: '-0.02em',
        }}
      >
        {index}
      </span>
    </div>
  )
}

/* ─── Navigation（同页视图切换：点"关于我们"隐藏首页内容显示 About，导航常驻不跳转） ─── */
function Nav({ onLogin, view, onView }: { onLogin: () => void; view: 'home' | 'about'; onView: (v: 'home' | 'about') => void }) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  // 首页滚动高亮区块（初始默认"功能"高亮，进入页面即有选中态）
  const [active, setActive] = useState<string>('features')
  // 仅"点击导航触发的程序化滚动"期间锁定高亮；用户手动滚动始终正常跟随
  const lockRef = useRef(false)
  const lockTimer = useRef<ReturnType<typeof setTimeout>>()

  /** 锁定高亮：点击导航时调用；程序化滚动停止（200ms 无滚动）或 200ms 无滚动即解锁 */
  const armLock = () => {
    lockRef.current = true
    clearTimeout(lockTimer.current)
    lockTimer.current = setTimeout(() => { lockRef.current = false }, 200)
  }

  // 程序化滚动中持续重置解锁计时；用户手动滚动（lockRef false）不影响高亮跟随
  useEffect(() => {
    const onScroll = () => {
      if (!lockRef.current) return
      clearTimeout(lockTimer.current)
      lockTimer.current = setTimeout(() => { lockRef.current = false }, 200)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      clearTimeout(lockTimer.current)
    }
  }, [])

  // 功能/故事/定价为首页锚点；关于我们为同页视图切换
  const links = [
    { key: 'features', href: '#features' },
    { key: 'story', href: '#story' },
    { key: 'pricing', href: '#pricing' },
    { key: 'about' },
  ]

  // 首页视图时监听滚动高亮（about 视图不监听，避免切换时跳动；滚动中锁定不切换）
  useEffect(() => {
    if (view !== 'home' || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !lockRef.current) setActive(e.target.id)
        })
      },
      { rootMargin: '-40% 0px -55% 0px' },
    )
    const ids = ['features', 'story', 'pricing']
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [view])

  const isActiveItem = (key: string) => (view === 'about' ? key === 'about' : key !== 'about' && active === key)

  const scrollToSection = (id: string) => {
    // 等 home 内容渲染（从 about 切回时）后滚动到对应区块
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    })
  }

  const handleNav = (l: { key: string }) => {
    setOpen(false)
    // 程序化滚动期间锁定高亮（避免经过相邻区块时来回跳）；手动滚动不受影响
    armLock()
    if (l.key === 'about') {
      onView('about')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    onView('home')
    // 点击立即高亮该项（即使在顶部无滚动，也有选中反馈）
    setActive(l.key)
    if (l.key === 'features') {
      // 功能 → 跳到页面最顶端
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      // 故事/定价 → 跳到对应卡片区块
      scrollToSection(l.key)
    }
  }

  return (
    <header className="sticky top-0 z-50" style={{ background: CEMENT, borderBottom: `1px solid ${PALE}` }}>
      <div className="max-w-7xl mx-auto px-6 h-[60px] flex items-center justify-between">
        <a href="#" style={{ fontFamily: "'Bebas Neue', sans-serif", color: CARBON, letterSpacing: '0.04em' }} className="text-2xl leading-none">
          {t('landing.brand')}
        </a>
        <nav className="hidden md:flex items-center gap-1 rounded-full px-2 py-1" style={{ background: 'rgba(17,17,17,0.07)', border: `1px solid ${PALE}` }}>
          {links.map((l) => {
            const isActive = isActiveItem(l.key)
            return (
              <a
                key={l.key}
                href={l.href ?? '/#'}
                onClick={(e) => { e.preventDefault(); handleNav(l) }}
                className="px-4 py-1.5 rounded-full text-sm font-medium transition-all"
                style={{ color: isActive ? '#ffffff' : MID, background: isActive ? CARBON : 'transparent' }}
              >
                {t(`landing.nav.${l.key}`)}
              </a>
            )
          })}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {/* 语言切换：与系统 5 语言一致 */}
          <select
            value={i18n.language}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            className="text-sm border rounded-full px-3 py-1.5"
            style={{ color: MID, borderColor: PALE, background: 'transparent' }}
            aria-label={t('landing.nav.languageAria')}
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>{t(`languages.${lang}`)}</option>
            ))}
          </select>
          <button
            className="rounded-full px-5 py-2 text-sm font-semibold transition-all hover:opacity-80"
            style={{ background: CARBON, color: '#ffffff' }}
            onClick={onLogin}
          >
            {t('landing.nav.getStarted')}
          </button>
        </div>
        <button className="md:hidden" style={{ color: CARBON }} onClick={() => setOpen(!open)} aria-label={t('landing.nav.menuAria')}>
          <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
            {open ? (
              <>
                <line x1="5" y1="5" x2="17" y2="17" />
                <line x1="17" y1="5" x2="5" y2="17" />
              </>
            ) : (
              <>
                <line x1="4" y1="7" x2="18" y2="7" />
                <line x1="4" y1="12" x2="18" y2="12" />
                <line x1="4" y1="17" x2="18" y2="17" />
              </>
            )}
          </svg>
        </button>
      </div>
      {open && (
        <div className="md:hidden px-6 pb-4 flex flex-col gap-3" style={{ borderTop: `1px solid ${PALE}` }}>
          {links.map((l) => {
            const isActive = isActiveItem(l.key)
            return (
              <a
                key={l.key}
                href={l.href ?? '/#'}
                className="text-sm font-medium py-1 rounded-lg px-2"
                style={{ color: isActive ? '#ffffff' : MID, background: isActive ? CARBON : 'transparent' }}
                onClick={(e) => { e.preventDefault(); handleNav(l) }}
              >
                {t(`landing.nav.${l.key}`)}
              </a>
            )
          })}
          <button className="rounded-full px-5 py-2 text-sm font-semibold self-start" style={{ background: CARBON, color: '#fff' }} onClick={onLogin}>
            {t('landing.nav.getStarted')}
          </button>
        </div>
      )}
    </header>
  )
}

/* ─── Hero ─── */
function Hero({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation()
  return (
    <section style={{ background: CEMENT }} className="px-4 pt-6 pb-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-center leading-none mb-4 tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(64px, 12vw, 160px)', color: CARBON, letterSpacing: '-0.01em' }}>
          {t('landing.hero.title')}
        </h1>
        <div className="relative rounded-3xl overflow-hidden w-full" style={{ background: CARBON, minHeight: 520 }}>
          {/* 本地化：设计稿 Unsplash 图下载到 /images/hero.jpg（零外网依赖） */}
          <img
            src="/images/hero.jpg"
            alt={t('landing.hero.imgAlt')}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: 0.35 }}
          />
          <div className="relative z-10 flex flex-col justify-between h-full p-8 md:p-12" style={{ minHeight: 520 }}>
            <div className="flex justify-between items-start">
              <PillBadge dark>{t('landing.hero.badge')}</PillBadge>
              <div className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)' }}>
                {t('landing.hero.est')}
              </div>
            </div>
            <div className="max-w-2xl">
              <p className="text-white/60 text-sm font-medium uppercase tracking-widest mb-4">{t('landing.hero.kicker')}</p>
              <p className="text-white text-xl md:text-2xl font-medium leading-snug mb-8 max-w-xl">{t('landing.hero.subtitle')}</p>
              <div className="flex items-center gap-4 flex-wrap">
                <button className="rounded-full px-7 py-3 font-semibold text-sm transition-all hover:bg-white/90" style={{ background: '#ffffff', color: CARBON }} onClick={onLogin}>
                  {t('landing.hero.cta')}
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6">
                <div className="flex -space-x-2">
                  {['AC', 'JK', 'MR', 'SW'].map((init, i) => (
                    <div key={init} className="w-9 h-9 rounded-full border-2 border-[#111] flex items-center justify-center text-[10px] font-bold text-white" style={{ background: ['#2563EB', '#10B981', '#F59E0B', '#EF4444'][i] }}>
                      {init}
                    </div>
                  ))}
                </div>
                <span className="text-white/50 text-sm">{t('landing.hero.socialProof')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Feature /01 — Calendar ─── */
function BookingWidget() {
  const { t } = useTranslation()
  const slots = [
    { time: '07:00', client: t('landing.f1.slotClient1'), status: 'confirmed' },
    { time: '09:30', client: t('landing.f1.slotClient2'), status: 'pending' },
    { time: '11:00', client: t('landing.f1.available'), status: 'free' },
    { time: '14:00', client: t('landing.f1.slotClient3'), status: 'confirmed' },
    { time: '17:30', client: t('landing.f1.slotClient4'), status: 'confirmed' },
  ]
  const colors: Record<string, string> = { confirmed: '#10B981', pending: '#F59E0B', free: 'rgba(255,255,255,0.2)' }
  const labels: Record<string, string> = { confirmed: t('landing.f1.confirmed'), pending: t('landing.f1.pending'), free: t('landing.f1.open') }

  return (
    <div className="rounded-2xl p-5 flex flex-col gap-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-white/50 text-xs font-semibold uppercase tracking-widest">{t('landing.f1.dateLabel')}</span>
        <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold" style={{ background: '#10B981', color: '#fff' }}>
          {t('landing.f1.live')}
        </span>
      </div>
      {slots.map((s) => (
        <div key={s.time} className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors[s.status] }} />
            <span className="text-white/40 text-xs font-mono">{s.time}</span>
            <span className={`text-sm font-medium ${s.status === 'free' ? 'text-white/25 italic' : 'text-white'}`}>{s.client}</span>
          </div>
          <span className="text-[10px] font-semibold" style={{ color: colors[s.status] }}>{labels[s.status]}</span>
        </div>
      ))}
    </div>
  )
}

function FeatureCalendar() {
  const { t } = useTranslation()
  return (
    <section id="features" style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-3xl p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center" style={{ background: CARBON }}>
          <div>
            <SectionBar badge={t('landing.f1.badge')} index="/01" dark />
            <h2 className="leading-none mb-5" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(42px, 6vw, 80px)', color: '#ffffff', letterSpacing: '-0.01em' }}>
              {t('landing.f1.title')}
            </h2>
            <p className="text-white/55 text-base leading-relaxed mb-8 max-w-md">{t('landing.f1.desc')}</p>
            <div className="flex flex-wrap gap-2">
              {['Long Press Booking', 'Conflict Guard', 'One-Tap Reschedule', 'Color-Coded Status'].map((tag) => (
                <PillBadge key={tag} dark>{t(`landing.f1.tag.${tag.toLowerCase().replace(/[^a-z0-9]/g, '')}`)}</PillBadge>
              ))}
            </div>
          </div>
          <div><BookingWidget /></div>
        </div>
      </div>
    </section>
  )
}

/* ─── Feature /02 — Packages ─── */
function PackageTracker() {
  const { t } = useTranslation()
  const clients = [
    { name: t('landing.f2.client1'), sessions: 12, status: t('landing.f2.safe'), color: '#10B981' },
    { name: t('landing.f2.client2'), sessions: 3, status: t('landing.f2.low'), color: '#F59E0B' },
    { name: t('landing.f2.client3'), sessions: 0, status: t('landing.f2.out'), color: '#EF4444' },
    { name: t('landing.f2.client4'), sessions: 8, status: t('landing.f2.safe'), color: '#10B981' },
    { name: t('landing.f2.client5'), sessions: 2, status: t('landing.f2.low'), color: '#F59E0B' },
  ]
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: CARBON, border: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <span className="text-white font-semibold text-sm">{t('landing.f2.trackerTitle')}</span>
        <div className="flex gap-3 text-[11px] font-medium">
          <span style={{ color: '#10B981' }}>● {t('landing.f2.safe')} (&gt;5)</span>
          <span style={{ color: '#F59E0B' }}>● {t('landing.f2.low')} (2–5)</span>
          <span style={{ color: '#EF4444' }}>● {t('landing.f2.out')} (0)</span>
        </div>
      </div>
      {clients.map((c) => (
        <div key={c.name} className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ background: c.color + '33' }}>
              {c.name.split(' ').map((n) => n[0]).join('')}
            </div>
            <span className="text-white text-sm font-medium">{c.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/40 text-xs font-mono">{c.sessions} {t('landing.f2.sessions')}</span>
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase" style={{ background: c.color + '22', color: c.color }}>{c.status}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

function FeaturePackages() {
  const { t } = useTranslation()
  return (
    <section id="features-2" style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-3xl overflow-hidden grid md:grid-cols-2" style={{ background: '#F0F0F0', border: `1px solid ${PALE}` }}>
          <div className="relative flex items-center justify-center" style={{ minHeight: 460, background: '#1a1a1a' }}>
            {/* 本地化：设计稿图下载到 /images/packages.jpg */}
            <img
              src="/images/packages.jpg"
              alt={t('landing.f2.imgAlt')}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ opacity: 0.7 }}
            />
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl p-4" style={{ background: 'rgba(17,17,17,0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)' }}>
              <SectionBar badge={t('landing.f2.badge')} index="/02" dark />
              <h3 className="text-white leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', letterSpacing: '-0.01em' }}>
                {t('landing.f2.title')}
              </h3>
            </div>
          </div>
          <div className="flex flex-col justify-center p-8 md:p-10" style={{ background: CEMENT }}>
            <p className="text-sm text-[#777] leading-relaxed mb-6 max-w-xs">{t('landing.f2.desc')}</p>
            <PackageTracker />
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Feature /03 — Cancellation ─── */
function FeatureCancellation() {
  const { t } = useTranslation()
  return (
    <section id="features-3" style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-3xl p-8 md:p-12" style={{ background: CARBON }}>
          <SectionBar badge={t('landing.f3.badge')} index="/03" dark />
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="leading-none mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(42px, 6vw, 76px)', color: '#ffffff', letterSpacing: '-0.01em' }}>
                {t('landing.f3.title')}
              </h2>
              <p className="text-white/55 leading-relaxed text-base mb-8">{t('landing.f3.desc')}</p>
              <div className="flex flex-wrap gap-2 mb-8">
                {['Self-Booking Link', 'Auto Reminders', 'No Sign-Up', 'Churn Alerts', 'Email Confirm'].map((tag) => (
                  <PillBadge key={tag} dark>{t(`landing.f3.tag.${tag.toLowerCase().replace(/[^a-z0-9]/g, '')}`)}</PillBadge>
                ))}
              </div>
              <div className="inline-flex items-center gap-3 rounded-2xl px-5 py-4" style={{ background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                <span className="text-3xl font-black text-[#10B981]" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '-0.02em' }}>{t('landing.f3.hoursSaved')}</span>
                <span className="text-white/60 text-sm leading-snug">{t('landing.f3.savedPerWeek')}</span>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg" style={{ background: 'rgba(255,255,255,0.1)' }}>🔁</div>
                  <div>
                    <p className="text-white font-semibold mb-1">{t('landing.f3.card1Title')}</p>
                    <p className="text-white/45 text-sm leading-relaxed">{t('landing.f3.card1Desc')}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl p-6" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg" style={{ background: 'rgba(239,68,68,0.15)' }}>⏱</div>
                  <div>
                    <p className="text-white font-semibold mb-1">{t('landing.f3.card2Title')}</p>
                    <p className="text-white/45 text-sm leading-relaxed">{t('landing.f3.card2Desc')}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden" style={{ background: '#1a1a1a', minHeight: 180 }}>
                {/* 本地化：设计稿图下载到 /images/cancellation.jpg */}
                <img
                  src="/images/cancellation.jpg"
                  alt={t('landing.f3.imgAlt')}
                  className="w-full h-full object-cover"
                  style={{ opacity: 0.6 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Story /04 ─── */
function Story() {
  const { t } = useTranslation()
  const stats = [
    { n: '14', unit: t('landing.story.unit1'), label: t('landing.story.stat1') },
    { n: '3', unit: t('landing.story.unit2'), label: t('landing.story.stat2') },
    { n: '$0', unit: '', label: t('landing.story.stat3') },
    { n: '5', unit: t('landing.story.unit3'), label: t('landing.story.stat4') },
  ]
  return (
    <section id="story" style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-3xl p-8 md:p-14" style={{ background: '#F5F5F5', border: `1px solid ${PALE}` }}>
          <SectionBar badge={t('landing.story.badge')} index="/04" />
          <div className="grid md:grid-cols-2 gap-14 items-start">
            <div>
              <h2 className="leading-none mb-8" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(44px, 6vw, 84px)', color: CARBON, letterSpacing: '-0.01em' }}>
                {t('landing.story.title')}
              </h2>
              <p className="text-[#444] leading-relaxed text-base mb-5">{t('landing.story.para1')}</p>
              <p className="text-[#444] leading-relaxed text-base mb-8">{t('landing.story.para2')}</p>
              <div className="rounded-2xl px-6 py-4 flex items-center gap-6 flex-wrap" style={{ background: 'rgba(17,17,17,0.05)', border: `1px solid ${PALE}` }}>
                <span className="text-xs font-semibold uppercase tracking-widest text-[#999]">{t('landing.story.integratesWith')}</span>
                {['Google', 'Gmail', 'Zoom', 'CSV'].map((b) => (
                  <span key={b} className="text-sm font-bold" style={{ color: '#666' }}>{b}</span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl p-6 flex flex-col justify-between" style={{ background: CARBON, minHeight: 140 }}>
                  <div className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '56px', color: '#ffffff', letterSpacing: '-0.02em' }}>
                    {s.n}
                    <span className="text-3xl text-white/40">{s.unit}</span>
                  </div>
                  <p className="text-white/40 text-xs font-semibold uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Pricing ─── */
function Pricing({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation()
  // OPT-040：落地页定价只显示可售两档（free/starter，由后端 sellable 标志过滤）
  const { data } = useQuery({ queryKey: ['subscription', 'pricing'], queryFn: () => api.getSubscriptionPricing(), staleTime: 60 * 1000 })
  const plans = (data || [])
    .filter((p) => p.sellable)
    .map((p) => ({
      // OPT-048：套餐名/tagline/功能列表全部走 i18n（对齐 SubscriptionCard 查表方式），
      // 后端英文串仅作 i18n key / defaultValue 回退
      tier: p.tier,
      name: t(`subscription.tier.${p.tier}`),
      price: `$${Number(p.priceMonthly).toFixed(2)}`,
      sub: t('common.perMonth'),
      tagline: t(`landing.pricing.${p.tier}.tagline`, { defaultValue: p.tagline }),
      features: p.features.map((f) => t(`subscription.${f}`, { defaultValue: f })),
      available: p.available,
      cta: p.tier === 'free' ? t('landing.pricing.free.cta') : t('landing.pricing.starter.cta'),
      dark: p.tier === 'starter',
      comingSoon: p.comingSoon || false,
    }))
  return (
    <section id="pricing" style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: MID }}>{t('landing.pricing.label')}</p>
          <h2 className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(48px, 8vw, 100px)', color: CARBON, letterSpacing: '-0.01em' }}>
            {t('landing.pricing.headline')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          {plans.map((p) => (
            <div key={p.tier} className="rounded-3xl p-6 flex flex-col gap-5 relative" style={{ background: p.dark ? CARBON : '#F5F5F5', border: p.dark ? 'none' : `1px solid ${PALE}` }}>
              {p.dark && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide" style={{ background: '#10B981', color: '#fff' }}>{t('landing.pricing.mostPopular')}</span>
                </div>
              )}
              {p.comingSoon && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide" style={{ background: '#6B7280', color: '#fff' }}>{t('landing.pricing.comingSoon')}</span>
                </div>
              )}
              <div>
                <p className={`text-[10px] font-semibold uppercase tracking-widest mb-2 ${p.dark ? 'text-white/35' : 'text-[#999]'}`}>{p.tagline}</p>
                <h3 className="leading-none mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: p.dark ? '#fff' : CARBON, letterSpacing: '0.02em' }}>{p.name}</h3>
                <div className="flex items-end gap-1">
                  <span className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: p.dark ? '#fff' : CARBON, letterSpacing: '-0.02em' }}>{p.price}</span>
                  <span className={`text-sm pb-2 ${p.dark ? 'text-white/35' : 'text-[#999]'}`}>{p.sub}</span>
                </div>
              </div>
              <div style={{ height: 1, background: p.dark ? 'rgba(255,255,255,0.08)' : PALE }} />
              <ul className="flex flex-col gap-2.5 flex-1">
                {p.features.map((f, i) => (
                  <li key={f} className="flex items-center gap-2.5">
                    <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: p.dark ? 'rgba(255,255,255,0.1)' : 'rgba(17,17,17,0.08)' }}>
                      {p.available?.[i] ? (
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><polyline points="1.5,4 3,5.5 6.5,2" stroke={p.dark ? '#10B981' : CARBON} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      ) : (
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><line x1="2" y1="4" x2="6" y2="4" stroke={p.dark ? '#6B7280' : '#9CA3AF'} strokeWidth="1.4" strokeLinecap="round" /></svg>
                      )}
                    </span>
                    <span className={`text-sm ${p.dark ? (p.available?.[i] ? 'text-white/65' : 'text-white/30') : (p.available?.[i] ? 'text-[#555]' : 'text-[#999]')}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                className="w-full rounded-full py-3 text-sm font-semibold transition-all hover:opacity-85"
                style={{ background: p.dark ? '#ffffff' : CARBON, color: p.dark ? CARBON : '#ffffff', ...(p.comingSoon ? { background: '#E5E7EB', color: '#9CA3AF', cursor: 'not-allowed' } : {}) }}
                disabled={p.comingSoon}
                onClick={onLogin}
              >
                {p.cta}
              </button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#999] mt-6">{t('landing.pricing.footnote')}</p>
      </div>
    </section>
  )
}

/* ─── Footer（导航支持同页视图切换） ─── */
function Footer({ onLogin, onView }: { onLogin: () => void; onView: (v: 'home' | 'about') => void }) {
  const { t } = useTranslation()

  const handleFooterNav = (key: string) => {
    if (key === 'about') {
      onView('about')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    onView('home')
    if (key === 'features') {
      // 功能 → 跳到页面最顶端
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      // 故事/定价 → 跳到对应卡片区块
      requestAnimationFrame(() => {
        document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' })
      })
    }
  }

  return (
    <footer style={{ background: CARBON }} className="px-4 pt-16 pb-10">
      <div className="max-w-7xl mx-auto">
        <div className="w-full text-center leading-none mb-12 select-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(48px, 10vw, 130px)', color: 'rgba(255,255,255,0.08)', letterSpacing: '0.08em' }}>
          {t('landing.brand')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', color: '#fff', letterSpacing: '0.05em' }} className="mb-3">{t('landing.brand')}</p>
            <p className="text-white/35 text-sm leading-relaxed max-w-xs">{t('landing.footer.desc')}</p>
          </div>
          <div>
            <p className="text-white/30 text-xs font-semibold uppercase tracking-widest mb-4">{t('landing.footer.navigation')}</p>
            <div className="flex flex-col gap-2.5">
              {['features', 'story', 'pricing'].map((l) => (
                <a
                  key={l}
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleFooterNav(l) }}
                  className="text-white/50 text-sm hover:text-white transition-colors"
                >
                  {t(`landing.nav.${l}`)}
                </a>
              ))}
              {/* OPT-041：关于我们 → 同页切换视图 */}
              <button onClick={() => handleFooterNav('about')} className="text-white/50 text-sm hover:text-white transition-colors text-left">{t('landing.nav.about')}</button>
              <button onClick={onLogin} className="text-white/50 text-sm hover:text-white transition-colors text-left">{t('landing.nav.getStarted')}</button>
            </div>
          </div>
          <div>
            <p className="text-white/30 text-xs font-semibold uppercase tracking-widest mb-4">{t('landing.footer.connect')}</p>
            <div className="flex flex-col gap-2.5 mb-5">
              <a href="mailto:hi@klikpt.vip" className="text-white/50 text-sm hover:text-white transition-colors">hi@klikpt.vip</a>
              <span className="text-white/50 text-sm">{t('landing.footer.coaches')}</span>
            </div>
            <div className="flex gap-3">
              {['IG', 'TW'].map((s) => (
                <a key={s} href="#" className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all hover:bg-white/20" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>{s}</a>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 pt-6">
          <p className="text-white/25 text-xs">{t('landing.footer.copyright')}</p>
          <div className="flex gap-5">
            {/* OPT-054：接通 Privacy/Terms 死链 → 公开法律页 */}
            <Link to="/privacy" className="text-white/25 text-xs hover:text-white/50 transition-colors">{t('landing.footer.privacy')}</Link>
            <Link to="/terms" className="text-white/25 text-xs hover:text-white/50 transition-colors">{t('landing.footer.terms')}</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

/* ─── Root（同页视图切换：home 首页 / about 关于我们，导航常驻不跳转） ─── */
export function LandingPage() {
  const [loginOpen, setLoginOpen] = useState(false)
  const [view, setView] = useState<'home' | 'about'>('home')
  return (
    <div style={{ background: CEMENT, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Nav onLogin={() => setLoginOpen(true)} view={view} onView={setView} />
      <main>
        {view === 'home' ? (
          <>
            <Hero onLogin={() => setLoginOpen(true)} />
            <FeatureCalendar />
            <FeaturePackages />
            <FeatureCancellation />
            <Story />
            <Pricing onLogin={() => setLoginOpen(true)} />
          </>
        ) : (
          <AboutSections onLogin={() => setLoginOpen(true)} />
        )}
      </main>
      <Footer onLogin={() => setLoginOpen(true)} onView={setView} />
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  )
}
